import checkSubVars from "./checkSubVars"
import { AppState } from "../data/redux-state"
import { IntlShape } from "gatsby-plugin-intl"

export const coVarKeyMainObservation = "mainObservation"
export const coVarKeyPrimaryTS = "PrimaryTS"
const sspPrefix = "SSP"
const sspMergedCoVar = "andSSPScens"

const specialCaseSourceKeys = [
  "p95",
  "p05",
  "sdevRange",
  "Data",
  "PrimaryTS",
  "Metadata",
  "geoRegionKey",
  "subVarKey",
]

export function isSpecialCaseSourceKey(key: string): boolean {
  return specialCaseSourceKeys.includes(key)
}

export function isMainObservationCoVarKey(key: string): boolean {
  return key === coVarKeyMainObservation || key === coVarKeyPrimaryTS
}

export function reorderCoVarOptions(coVarOptions: CoVarOption[]): CoVarOption[] {
  const coVarsJsonOrder = coVarOptions.filter((coVar) => {
    return (
      !isMainObservationCoVarKey(coVar.value) && coVar.value !== sspMergedCoVar
    )
  })
  const coVarMainObservation = coVarOptions.filter((coVar) => {
    return isMainObservationCoVarKey(coVar.value)
  })
  const coVarsSSP = coVarOptions.filter((coVar) => {
    return coVar.value === sspMergedCoVar
  })
  return [...coVarMainObservation, ...coVarsJsonOrder, ...coVarsSSP]
}

function sspMergedCoVars(
  coVarOptions: CoVarOption[],
  intl: IntlShape
): CoVarOption[] {
  const coVarsWithoutSSP = coVarOptions.filter((coVar) => {
    return false === coVar.value.startsWith(sspPrefix)
  })
  if (coVarsWithoutSSP.length === coVarOptions.length) {
    return coVarOptions
  }
  return [
    ...coVarsWithoutSSP,
    {
      value: sspMergedCoVar,
      name: intl.formatMessage({ id: "coVariables.andSSPScens" }),
      sdev: false,
      ur: false,
      sdevEnabled: false,
      urEnabled: false,
      hasData: true,
    },
  ]
}

export function createSelectableCoVarOptions(
  appState: AppState,
  coVarOptions: CoVarOption[],
  intl: IntlShape
): CoVarOption[] {
  if (appState.indicatorClass !== "CWM-GLO") {
    return coVarOptions
  }
  const mergedCoVars = sspMergedCoVars(coVarOptions, intl)
  return reorderCoVarOptions(mergedCoVars)
}

export function orderSelectedCoVarsByDropdownOrder(args: {
  appState: AppState
  selectedCoVars: CoVarOption[]
  availableCoVars: CoVarOption[]
  intl: IntlShape
}): CoVarOption[] {
  const selectableCoVars = createSelectableCoVarOptions(
    args.appState,
    args.availableCoVars,
    args.intl
  )
  const order = new Map(
    selectableCoVars.map((coVar, index) => [coVar.value, index])
  )
  return [...args.selectedCoVars].sort((left, right) => {
    return (
      (order.get(left.value) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right.value) ?? Number.MAX_SAFE_INTEGER)
    )
  })
}

export interface CoVarOption {
  value: string
  name: string
  sdev: boolean // standarderror bars
  ur: boolean // 90% uncertainty range
  sdevEnabled: boolean
  urEnabled: boolean
  hasData: boolean
  auxiliaryGroup?: boolean // true if this is a merged auxiliary coVar group (e.g. "CCMean" merging CCMean_PrimaryTS, CCMean_SDTh25C, ...)
  auxiliaryGroupIsSuffix?: boolean // true when the group name is the suffix (new format: subVar_group, e.g. PrimaryTS_CCMean); false when prefix (old format: group_subVar)
  sdevLabelId?: string // translation key for the StDev button label
  urLabelId?: string // translation key for the UR button label
}

function determineSdevLabelId(coVar: CoVar): string | undefined {
  if (coVar.AnnValStDev_lower && coVar.AnnValStDev_upper)
    return "coVariables.AnnValStDev"
  if (coVar.AGRMeanStDev_lower && coVar.AGRMeanStDev_upper)
    return "coVariables.AGRMeanStDev"
  if (coVar.NatVarStDev_lower && coVar.NatVarStDev_upper)
    return "coVariables.NatVarStDev"
  if (coVar.GRsSpreadStDev_lower && coVar.GRsSpreadStDev_upper)
    return "coVariables.GRSpreadStDev"
  if (coVar.sDevRange) return "coVariables.StDev"
  return undefined
}

function determineUrLabelId(coVar: CoVar): string | undefined {
  if (coVar.AGRMean90PCI_p05 && coVar.AGRMean90PCI_p95)
    return "coVariables.AGRMean90PCI"
  if (coVar.NatVar90PCI_p05 && coVar.NatVar90PCI_p95)
    return "coVariables.NatVar90PCI"
  if (coVar.GRsSpread90PCI_p05 && coVar.GRsSpread90PCI_p95)
    return "coVariables.GRSpread90PCI"
  if (coVar.p05 && coVar.p95) return "coVariables.90PCI"
  return undefined
}

function hasNonNullData(data: GeoRegionData | undefined): boolean {
  if (!data) return false
  return Object.values(data).some((v) => v !== null && v !== undefined)
}

export function extractCoVarOptions(
  appState: AppState,
  intl: IntlShape
): CoVarOption[] {
  return extractRawCoVarOptions({
    selectedRegions: appState.geoRegion,
    selectedSubVars: appState.subVariable,
    sourceJson: appState.sourceJson,
    intl: intl,
  })
}

export function extractRawCoVarOptions(args: {
  selectedRegions: string[]
  selectedSubVars: string[]
  sourceJson: GeoVarJson | null
  intl: IntlShape
}): CoVarOption[] {
  const sourceJson = args.sourceJson
  if (!sourceJson) {
    return []
  }
  const selectedRegions = args.selectedRegions
  const selectedSubVars = args.selectedSubVars.filter((sv) => !!sv)
  const intl = args.intl
  if (selectedSubVars.length === 0) {
    return []
  }
  const isV3Json = sourceJson.FileMetadata.IndicatorsClass.startsWith("EWM")
  const mainSubvars = checkSubVars(sourceJson)
  const geoRegions: GeoRegion[] = []
  const mainObservationKey = isV3Json
    ? coVarKeyPrimaryTS
    : coVarKeyMainObservation
  const mainObservationName =
    mainObservationKey === coVarKeyPrimaryTS
      ? intl.formatMessage({ id: "coVariables." + coVarKeyPrimaryTS })
      : intl.formatMessage({
          id: "coVariables." + coVarKeyMainObservation,
        })
  const mainObservationCoVar: CoVarOption = {
    value: mainObservationKey,
    name: mainObservationName,
    sdev: false,
    ur: false,
    sdevEnabled: false,
    urEnabled: false,
    hasData: true,
  }
  const coVars: CoVarOption[] = [mainObservationCoVar]
  const coVarKeys: Record<string, boolean> = { [mainObservationKey]: true }

  const extractOtherCoVariables = (region: GeoRegion) => {
    const regionWithCoVars = region as unknown as GeoRegionWithCoVars
    for (const key of Object.keys(regionWithCoVars)) {
      const maybeCoVar = regionWithCoVars[key]
      const coVar: CoVar | undefined =
        maybeCoVar && typeof maybeCoVar === "object" && "Data" in maybeCoVar
          ? (maybeCoVar as CoVar)
          : undefined
      if (coVar && coVar.Data && !isSpecialCaseSourceKey(key)) {
        if (!coVarKeys[key]) {
          // check to prevent duplicate coVars
          coVarKeys[key] = true
          coVars.push({
            value: key,
            name: intl.formatMessage({ id: "coVariables." + key }),
            sdev:
              !!coVar.sDevRange ||
              (!!coVar.GRsSpreadStDev_lower && !!coVar.GRsSpreadStDev_upper) ||
              (!!coVar.AGRMeanStDev_lower && !!coVar.AGRMeanStDev_upper) ||
              (!!coVar.NatVarStDev_lower && !!coVar.NatVarStDev_upper) ||
              (!!coVar.AnnValStDev_lower && !!coVar.AnnValStDev_upper),
            ur:
              (!!coVar.p05 && !!coVar.p95) ||
              (!!coVar.GRsSpread90PCI_p05 && !!coVar.GRsSpread90PCI_p95) ||
              (!!coVar.AGRMean90PCI_p05 && !!coVar.AGRMean90PCI_p95) ||
              (!!coVar.NatVar90PCI_p05 && !!coVar.NatVar90PCI_p95),
            sdevEnabled: false,
            urEnabled: false,
            hasData: hasNonNullData(coVar.Data),
            sdevLabelId: determineSdevLabelId(coVar),
            urLabelId: determineUrLabelId(coVar),
          })
        }
      }
    }
  }

  if (selectedSubVars.length >= 1) {
    for (const subvar of selectedSubVars) {
      for (const region of selectedRegions) {
        geoRegions.push(mainSubvars[subvar].GeoRegions[region])
      }
    }
    for (const region of geoRegions) {
      const mainCoVar: CoVar | undefined = isV3Json
        ? (region as GeoRegionV3).PrimaryTS
        : {
            Data: (region as GeoRegionV2).Data,
            Metadata: (region as GeoRegionV2).Metadata,
            sDevRange: (region as GeoRegionV2).sdevRange?.Data,
            p05: (region as GeoRegionV2).p05?.Data,
            p95: (region as GeoRegionV2).p95?.Data,
          }
      if (
        mainCoVar?.sDevRange ||
        (mainCoVar?.GRsSpreadStDev_lower && mainCoVar?.GRsSpreadStDev_upper) ||
        (mainCoVar?.AnnValStDev_lower && mainCoVar?.AnnValStDev_upper)
      ) {
        mainObservationCoVar.sdev = true
        mainObservationCoVar.sdevLabelId =
          mainObservationCoVar.sdevLabelId ?? determineSdevLabelId(mainCoVar)
      }
      if (
        (mainCoVar?.p05 && mainCoVar?.p95) ||
        (mainCoVar?.GRsSpread90PCI_p05 && mainCoVar?.GRsSpread90PCI_p95) ||
        (mainCoVar?.AGRMean90PCI_p05 && mainCoVar?.AGRMean90PCI_p95) ||
        (mainCoVar?.NatVar90PCI_p05 && mainCoVar?.NatVar90PCI_p95)
      ) {
        mainObservationCoVar.ur = true
        mainObservationCoVar.urLabelId =
          mainObservationCoVar.urLabelId ?? determineUrLabelId(mainCoVar)
      }
      extractOtherCoVariables(region)
    }
  }
  return mergeAuxiliaryCoVarGroups(coVars, intl)
}

/**
 * Detects auxiliary coVar groups from two possible key formats:
 *  - Old format: `group_subVar` (e.g. CCMean_PrimaryTS) — suffix is a base key
 *  - New format: `subVar_group` (e.g. PrimaryTS_CCMean) — prefix is a base key
 * Returns merged options replacing the individual ones.
 */
function mergeAuxiliaryCoVarGroups(
  coVars: CoVarOption[],
  intl: IntlShape
): CoVarOption[] {
  // Collect "base" keys: non-compound coVar keys + PrimaryTS
  const baseKeys = new Set<string>()
  baseKeys.add(coVarKeyPrimaryTS)
  for (const cv of coVars) {
    if (!cv.value.includes("_") && !cv.auxiliaryGroup) {
      baseKeys.add(cv.value)
    }
  }

  // Group auxiliary coVars by their group name (detected as either prefix or suffix)
  const groups: Record<string, { members: CoVarOption[]; isSuffix: boolean }> =
    {}
  const nonAuxiliary: CoVarOption[] = []
  for (const cv of coVars) {
    const idx = cv.value.indexOf("_")
    if (idx > 0) {
      const prefix = cv.value.substring(0, idx)
      const suffix = cv.value.substring(idx + 1)
      if (baseKeys.has(suffix)) {
        // Old format: group_subVar (e.g. CCMean_PrimaryTS)
        if (!groups[prefix]) groups[prefix] = { members: [], isSuffix: false }
        groups[prefix].members.push(cv)
        continue
      } else if (baseKeys.has(prefix)) {
        // New format: subVar_group (e.g. PrimaryTS_CCMean)
        if (!groups[suffix]) groups[suffix] = { members: [], isSuffix: true }
        groups[suffix].members.push(cv)
        continue
      }
    }
    nonAuxiliary.push(cv)
  }

  // Only merge groups that have >= 2 members
  const merged: CoVarOption[] = [...nonAuxiliary]
  for (const [groupName, { members, isSuffix }] of Object.entries(groups)) {
    if (members.length >= 2) {
      merged.push({
        value: groupName,
        name: intl.formatMessage({ id: "coVariables." + groupName }),
        sdev: members.some((m) => m.sdev),
        ur: members.some((m) => m.ur),
        sdevEnabled: false,
        urEnabled: false,
        hasData: members.some((m) => m.hasData),
        auxiliaryGroup: true,
        auxiliaryGroupIsSuffix: isSuffix,
        sdevLabelId: members.find((m) => m.sdevLabelId)?.sdevLabelId,
        urLabelId: members.find((m) => m.urLabelId)?.urLabelId,
      })
    } else {
      merged.push(...members)
    }
  }
  return merged
}

/**
 * Given the selected coVarOptions (which may include merged auxiliary groups),
 * returns the expanded set of actual coVar keys to use for line filtering.
 * Merged keys like "CCMean" expand to the individual JSON keys based on format:
 *  - New format (auxiliaryGroupIsSuffix=true): "PrimaryTS_CCMean", "SDTh25C_CCMean", ...
 *  - Old format (auxiliaryGroupIsSuffix=false): "CCMean_PrimaryTS", "CCMean_SDTh25C", ...
 */
export function expandAuxiliaryCoVarKeys(
  coVarOptions: CoVarOption[]
): string[] {
  const selectedNonAux = coVarOptions
    .filter((o) => !o.auxiliaryGroup)
    .map((o) => o.value)
  const expanded: string[] = []
  for (const option of coVarOptions) {
    if (option.auxiliaryGroup) {
      for (const mainKey of selectedNonAux) {
        if (option.auxiliaryGroupIsSuffix) {
          // New format: subVar_group (e.g. PrimaryTS_CCMean)
          expanded.push(mainKey + "_" + option.value)
        } else {
          // Old format: group_subVar (e.g. CCMean_PrimaryTS)
          expanded.push(option.value + "_" + mainKey)
        }
      }
    } else {
      expanded.push(option.value)
    }
  }
  return expanded
}

/**
 * Drops selected coVars that are currently grayed out in the droplist
 * (hasData === false for the present SubVariable/GeoRegion selection), so the
 * legend description matches the droplist graying (issue #119).
 */
export function filterCoVarsWithAvailableData(args: {
  selectedCoVars: CoVarOption[]
  availableCoVars: CoVarOption[]
}): CoVarOption[] {
  const availability = new Map(
    args.availableCoVars.map((coVar) => [coVar.value, coVar.hasData])
  )
  return args.selectedCoVars.filter(
    (coVar) => availability.get(coVar.value) !== false
  )
}

export function postprocessCoVarsDescription(
  coVarsDescription: string,
  intl: IntlShape
): string {
  const isDe = intl.locale === "de"
  const startCutAway = isDe ? "und " : "and "
  if (coVarsDescription.startsWith(startCutAway)) {
    coVarsDescription = coVarsDescription.slice(startCutAway.length)
    const startsWithLowercase =
      coVarsDescription.charAt(0) === coVarsDescription.charAt(0).toLowerCase()
    if (startsWithLowercase) {
      coVarsDescription =
        coVarsDescription.charAt(0).toUpperCase() + coVarsDescription.slice(1)
    }
  }
  return coVarsDescription
}

function formatEnabledCoVarQualifier(args: {
  coVar: CoVarOption
  type: "UR" | "sdev"
  intl: IntlShape
  isEwmIndicator: boolean
}): string {
  const { coVar, type, intl, isEwmIndicator } = args
  if (type === "UR" && !coVar.urEnabled) {
    return ""
  }
  if (type === "sdev" && !coVar.sdevEnabled) {
    return ""
  }

  if (isEwmIndicator) {
    const labelId = type === "UR"
      ? (coVar.urLabelId ?? "coVariables.90PCI")
      : (coVar.sdevLabelId ?? "coVariables.StDev")
    return (
      intl.formatMessage({ id: "coVariables.inclPrefix" }) +
      " " +
      intl.formatMessage({ id: labelId })
    )
  }

  return type === "UR"
    ? intl.formatMessage({ id: "coVariables.UR" })
    : intl.formatMessage({ id: "coVariables.sdev" })
}

export function formatSelectedCoVarsDescription(args: {
  appState: AppState
  coVars: CoVarOption[]
  intl: IntlShape
}): string {
  const { appState, coVars, intl } = args
  const isEwmIndicator = appState.indicatorClass.startsWith("EWM")
  let coVarsDescription = ""

  coVars.forEach((coVar: CoVarOption) => {
    if (coVar.value.startsWith("SSP")) {
      return
    }
    const mainEntry = intl.formatMessage({ id: "coVariables." + coVar.value })
    coVarsDescription += mainEntry + " "

    const urQualifier = formatEnabledCoVarQualifier({
      coVar,
      type: "UR",
      intl,
      isEwmIndicator,
    })
    if (urQualifier) {
      coVarsDescription += urQualifier + " "
    }

    // Exception: if the main droplist-entry string already conveys the StDev
    // (e.g. the EWM "and StDev ann.values vs dec.mean" entry, German "StAbw"),
    // do not append the redundant "incl. StDev" button qualifier.
    const mainEntryHasStDev =
      mainEntry.includes("StDev") || mainEntry.includes("StAbw")
    const sdevQualifier = mainEntryHasStDev
      ? ""
      : formatEnabledCoVarQualifier({
          coVar,
          type: "sdev",
          intl,
          isEwmIndicator,
        })
    if (sdevQualifier) {
      coVarsDescription += sdevQualifier + " "
    }
  })

  return postprocessCoVarsDescription(coVarsDescription, intl)
}
